import { Button, DropdownSelect, Modal } from '@components/common';
import { FormInput, FormTextarea } from '@components/common/FormField';
import React, { useState } from 'react';
import type {
  InventoryCategoryRecord,
  InventoryItemType,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';
import {
  INVENTORY_COSTING_METHOD_OPTIONS,
  INVENTORY_ITEM_TYPE_OPTIONS,
  InventoryItemFormState,
} from './inventoryItemForm';

interface InventoryItemEditorModalProps {
  initial: InventoryItemFormState;
  categories: InventoryCategoryRecord[];
  units: InventoryUnitRecord[];
  isSaving: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: InventoryItemFormState) => void;
}

export function InventoryItemEditorModal({
  initial,
  categories,
  units,
  isSaving,
  title,
  onClose,
  onSubmit,
}: InventoryItemEditorModalProps) {
  const [form, setForm] = useState<InventoryItemFormState>(initial);

  const set = <K extends keyof InventoryItemFormState>(
    key: K,
    value: InventoryItemFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Item name"
            required
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="e.g. Wheat Flour"
          />
          <FormInput
            label="SKU"
            value={form.sku}
            onChange={(event) => set('sku', event.target.value)}
            placeholder="Optional stock code"
          />
        </div>

        <div
          className={`grid gap-4 ${
            INVENTORY_COSTING_METHOD_OPTIONS.length > 1 ? 'md:grid-cols-2' : ''
          }`}
        >
          <div className="space-y-2">
            <DropdownSelect
              label="Category"
              value={form.categoryId}
              options={categories.map((category) => ({
                value: category.id,
                label: category.name,
              }))}
              placeholder="Select category"
              customDisplayLabel={
                form.newCategoryName.trim() && !form.categoryId
                  ? `New: ${form.newCategoryName.trim()}`
                  : undefined
              }
              onChange={(nextValue) => {
                set('categoryId', nextValue);
                set('newCategoryName', '');
              }}
              createConfig={{
                inputLabel: 'Add new category',
                inputPlaceholder: 'e.g. Dry Goods',
                value: form.newCategoryName,
                onValueChange: (nextValue) => set('newCategoryName', nextValue),
                onUseValue: (nextValue) => {
                  set('newCategoryName', nextValue);
                  set('categoryId', '');
                },
                actionLabel: `Use "${form.newCategoryName.trim()}"`,
              }}
            />
          </div>
          {INVENTORY_COSTING_METHOD_OPTIONS.length > 1 ? (
            <DropdownSelect
              label="Costing method"
              value={form.costingMethod}
              options={INVENTORY_COSTING_METHOD_OPTIONS}
              onChange={(nextValue) =>
                set('costingMethod', nextValue as 'weighted_average')
              }
            />
          ) : null}
        </div>

        <FormTextarea
          label="Description"
          value={form.description}
          onChange={(event) => set('description', event.target.value)}
          placeholder="Optional notes about the item"
          rows={3}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <DropdownSelect
              label="Purchase unit"
              value={form.purchaseUnitId}
              options={units.map((unit) => ({
                value: unit.id,
                label: unit.name,
              }))}
              placeholder="Select purchase unit"
              customDisplayLabel={
                form.newPurchaseUnitName.trim() && !form.purchaseUnitId
                  ? `New: ${form.newPurchaseUnitName.trim()}`
                  : undefined
              }
              onChange={(nextValue) => {
                set('purchaseUnitId', nextValue);
                set('newPurchaseUnitName', '');
              }}
              createConfig={{
                inputLabel: 'Add new purchase unit',
                inputPlaceholder: 'e.g. Bag',
                value: form.newPurchaseUnitName,
                onValueChange: (nextValue) =>
                  set('newPurchaseUnitName', nextValue),
                onUseValue: (nextValue) => {
                  set('newPurchaseUnitName', nextValue);
                  set('purchaseUnitId', '');
                },
                actionLabel: `Use "${form.newPurchaseUnitName.trim()}"`,
              }}
            />
          </div>
          <div className="space-y-2">
            <DropdownSelect
              label="Stock unit"
              value={form.stockUnitId}
              options={units.map((unit) => ({
                value: unit.id,
                label: unit.name,
              }))}
              placeholder="Select stock unit"
              customDisplayLabel={
                form.newStockUnitName.trim() && !form.stockUnitId
                  ? `New: ${form.newStockUnitName.trim()}`
                  : undefined
              }
              onChange={(nextValue) => {
                set('stockUnitId', nextValue);
                set('newStockUnitName', '');
              }}
              createConfig={{
                inputLabel: 'Add new stock unit',
                inputPlaceholder: 'e.g. Kilogram',
                value: form.newStockUnitName,
                onValueChange: (nextValue) =>
                  set('newStockUnitName', nextValue),
                onUseValue: (nextValue) => {
                  set('newStockUnitName', nextValue);
                  set('stockUnitId', '');
                },
                actionLabel: `Use "${form.newStockUnitName.trim()}"`,
              }}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Conversion rate"
            required
            type="number"
            min="0.000001"
            step="0.000001"
            value={form.conversionRate}
            onChange={(event) => set('conversionRate', event.target.value)}
            placeholder="How many stock units are in one purchase unit"
          />
          <FormInput
            label="Reorder level"
            required
            type="number"
            min="0"
            step="0.0001"
            value={form.reorderLevel}
            onChange={(event) => set('reorderLevel', event.target.value)}
            placeholder="Minimum stock level"
          />
        </div>

        <DropdownSelect
          label="Item type"
          value={form.itemType}
          options={INVENTORY_ITEM_TYPE_OPTIONS}
          onChange={(nextValue) =>
            set('itemType', nextValue as InventoryItemType)
          }
        />

        <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-700">
          <input
            type="checkbox"
            aria-label="Item is active"
            checked={form.isActive}
            onChange={(event) => set('isActive', event.target.checked)}
          />
          Item is active
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save item
          </Button>
        </div>
      </form>
    </Modal>
  );
}
